import React, { useState } from 'react';
import { useTranslation } from '../../hooks/useTranslation';
import { ChevronDown } from 'lucide-react';

// FAQ Page with simplified accordion design
interface FAQItemType {
  id: number;
  question: string;
  answer: string;
  category: string;
}

const FAQ: React.FC = () => {
  const { t } = useTranslation();
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const faqData: FAQItemType[] = [
    {
      id: 1,
      question: 'How does the cow dung collection process work?',
      answer: 'Our GauSakhi platform workers visit your farm, collect cow dung using digital weighing systems, and provide instant payment through our secure mobile app. The entire process is transparent and documented.',
      category: 'Collection'
    },
    {
      id: 2,
      question: 'How do I receive payment for cow dung?',
      answer: 'Payments are processed instantly through our digital payment system directly to your registered bank account or mobile wallet. You receive payment immediately after the dung is weighed and verified.',
      category: 'Payment'
    },
    {
      id: 3,
      question: 'What is the pricing for cow dung?',
      answer: 'Pricing varies based on quality, moisture content, and market conditions. Our transparent pricing system ensures fair rates for all farmers. Current rates are displayed in the app.',
      category: 'Pricing'
    },
    {
      id: 4,
      question: 'How do I register as a farmer?',
      answer: 'You can register through our mobile app or website. Simply provide your basic details, farm information, and bank account details. Our team will verify your information and activate your account within 24 hours.',
      category: 'Registration'
    },
    {
      id: 5,
      question: 'What happens to the collected cow dung?',
      answer: 'The collected cow dung is processed in our biogas plants to produce clean energy (biogas/CBG) and organic fertilizer. This helps in sustainable waste management and renewable energy generation.',
      category: 'Process'
    },
    {
      id: 6,
      question: 'Is there a minimum quantity requirement?',
      answer: 'No, there is no minimum quantity requirement. We collect cow dung from farms of all sizes, ensuring even small farmers can participate and benefit from our platform.',
      category: 'Requirements'
    }
  ];

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 py-16">
      <div className="container mx-auto px-4 py-8">
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold text-gray-800 mb-4">
            Frequently Asked Questions
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Find answers to common questions about our platform and services
          </p>
        </div>

        <div className="max-w-3xl mx-auto space-y-4">
          {faqData.map((faq, index) => (
            <div
              key={faq.id}
              className="bg-white rounded-lg shadow-md overflow-hidden transition-all duration-300 hover:shadow-lg"
            >
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full px-6 py-4 text-left flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-green-500"
                aria-expanded={openIndex === index}
              >
                <div className="flex-1">
                  <span className="text-sm font-medium text-green-600 uppercase tracking-wide">
                    {faq.category}
                  </span>
                  <h3 className="text-lg font-semibold text-gray-800 mt-1">
                    {faq.question}
                  </h3>
                </div>
                <ChevronDown
                  className={`w-5 h-5 text-gray-500 transition-transform duration-300 ${
                    openIndex === index ? 'transform rotate-180' : ''
                  }`}
                />
              </button>

              <div
                className={`px-6 overflow-hidden transition-all duration-300 ${
                  openIndex === index ? 'max-h-96 py-4' : 'max-h-0'
                }`}
              >
                <p className="text-gray-600 leading-relaxed">
                  {faq.answer}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-12">
          <p className="text-gray-600 mb-4 text-lg">
            Can't find what you're looking for?
          </p>
          <button className="bg-green-600 text-white px-8 py-3 rounded-lg hover:bg-green-700 transition-colors shadow-md hover:shadow-lg">
            Contact Support
          </button>
        </div>
      </div>
    </div>
  );
};

export default FAQ;